import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { Country } from '../../models/common/country.model';
import { isValidPhoneForCountry, normalizePhoneForCountry, phonePlaceholderForCountry } from '../utils/country-phone.util';

/**
 * A country-aware mobile field that follows PrimeNG form field UI design patterns
 * (iftalabel, inputgroup, icons) and always writes an international phone number
 * (for example, +966501234567) to its connected form control.
 */
@Component({
    selector: 'app-international-phone-field',
    standalone: true,
    imports: [CommonModule, InputGroupModule, InputGroupAddonModule, InputTextModule, IftaLabelModule],
    templateUrl: './international-phone-field.component.html',
    styleUrl: './international-phone-field.component.css',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InternationalPhoneFieldComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InternationalPhoneFieldComponent),
            multi: true
        }
    ]
})
export class InternationalPhoneFieldComponent implements ControlValueAccessor, Validator, OnChanges {
    @Input() country: Country | null | undefined;
    @Input() label = 'Mobile number';
    @Input() placeholder?: string;
    @Input() required = false;
    @Input() inputId = `international-phone-${Math.random().toString(36).slice(2)}`;

    // Flexibility inputs following PrimeNG form fields UI design pattern
    @Input() icon = 'pi pi-phone';
    @Input() showIcon = true;
    @Input() useIftaLabel = true;
    @Input() useInputGroup = true;
    @Input() dialingCodePlacement: 'separate' | 'combined' | 'none' = 'separate';
    @Input() showHelperText = false;
    @Input() helperText?: string;
    @Input() styleClass?: string;

    displayValue = '';
    disabled = false;

    private value: string | null = null;
    private onChange: (value: string | null) => void = () => undefined;
    private onTouched: () => void = () => undefined;
    private onValidatorChange: () => void = () => undefined;

    get dialingCode(): string | null {
        const digits = `${this.country?.phoneCode ?? ''}`.replace(/\D/g, '');
        return digits ? `+${digits}` : null;
    }

    get hasValue(): boolean {
        return !!this.value;
    }

    get inputPlaceholder(): string {
        if (this.placeholder) {
            return this.placeholder;
        }
        return this.dialingCode ? 'Mobile number' : phonePlaceholderForCountry(this.country);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['country'] && !changes['country'].firstChange && this.value) {
            this.setValue(this.value, true);
        }
        this.onValidatorChange();
    }

    writeValue(value: string | null | undefined): void {
        this.setValue(value ?? null, false);
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    validate(): ValidationErrors | null {
        if (!this.value) {
            return this.required ? { required: true } : null;
        }
        if (!this.dialingCode) {
            return { phoneCountryCodeRequired: true };
        }
        return isValidPhoneForCountry(this.value, this.country) ? null : { invalidInternationalPhone: true };
    }

    onInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.setValue(this.valueFromInput(input.value), true);
    }

    onBlur(): void {
        this.onTouched();
    }

    private setValue(value: string | null, emit: boolean): void {
        this.value = this.normalize(value);
        this.displayValue = this.localDisplayValue(this.value);
        if (emit) {
            this.onChange(this.value);
            this.onValidatorChange();
        }
    }

    private valueFromInput(value: string): string | null {
        const raw = value.trim();
        if (!raw) {
            return null;
        }
        const dialingDigits = this.dialingCode?.replace(/\D/g, '') ?? '';
        const typedDigits = raw.replace(/\D/g, '');
        if (!dialingDigits) {
            return raw;
        }
        const nationalDigits = typedDigits.startsWith(dialingDigits)
            ? typedDigits.slice(dialingDigits.length)
            : typedDigits;
        return `+${dialingDigits}${nationalDigits.replace(/^0+/, '')}`;
    }

    private normalize(value: string | null): string | null {
        if (!value?.trim()) {
            return null;
        }
        return normalizePhoneForCountry(value, this.country);
    }

    private localDisplayValue(value: string | null): string {
        if (!value) {
            return '';
        }
        const digits = value.replace(/\D/g, '');
        const dialingDigits = this.dialingCode?.replace(/\D/g, '') ?? '';
        return dialingDigits && digits.startsWith(dialingDigits)
            ? digits.slice(dialingDigits.length)
            : value;
    }
}
